import React from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Paper,
  alpha,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { format } from 'date-fns';
import { campaignApi } from '../services/campaignApi';
import CampaignResults from './CampaignResults';

interface CampaignDetailsProps {
  campaignId?: string | null;
  onBack: () => void;
}

interface Campaign {
  id: string;
  name: string;
  description: string;
  completedCalls: number;
  scheduledCalls: number;
}

interface CampaignContact {
  id: number;
  contact_list_id: number;
  contact_list_name?: string;
  phone_number: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  custom_fields: Record<string, any> | null;
  prompt_variables: Record<string, any>;
  call_status: {
    scheduled_call_id: string;
    call_attempted: boolean;
    call_placed: boolean | null;
    schedule_dt: string;
    context_parameters: Record<string, any>;
  } | null;
}

const CampaignDetails: React.FC<CampaignDetailsProps> = ({ campaignId, onBack }) => {
  const [campaign, setCampaign] = React.useState<Campaign | null>(null);
  const [contacts, setContacts] = React.useState<CampaignContact[]>([]);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [contactsLoading, setContactsLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    const fetchCampaignDetails = async () => {
      if (!campaignId) {
        setError("Campaign ID is missing.");
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const data = await campaignApi.getCampaigns();
        const campaignData = data.find((c: any) => c.id.toString() === campaignId);
        if (campaignData) {
          setCampaign({
            id: campaignData.id.toString(),
            name: campaignData.name || `Campaign ${campaignId}`,
            description: campaignData.description || '',
            completedCalls: campaignData.completedCalls || 0,
            scheduledCalls: campaignData.scheduledCalls || 0,
          });
        } else {
          setError('Campaign not found');
        }
      } catch (err: any) {
        setError(err.message || 'Failed to fetch campaign details');
      } finally {
        setLoading(false);
      }
    };

    const fetchContacts = async () => {
      if (!campaignId) return;
      try {
        setContactsLoading(true);
        const contactsData = await campaignApi.getCampaignContacts(campaignId);
        setContacts(contactsData);
      } catch (err: any) {
        console.error('Failed to fetch contacts:', err);
      } finally {
        setContactsLoading(false);
      }
    };

    fetchCampaignDetails();
    fetchContacts();
  }, [campaignId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!campaign) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <Alert severity="info">Campaign not found.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ color: alpha('#000', 0.6) }}>
          Back to Campaigns
        </Button>
      </Box>

      <Typography variant="h2" sx={{ mb: 4, fontWeight: 600 }}>
        {campaign.name}
      </Typography>

      {/* Campaign Description */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>Description</Typography>
        <Typography variant="body1" color="text.secondary">{campaign.description || 'No description'}</Typography>
      </Paper>

      {/* What the campaign did.
          Both panels here used to read from `metrics`, derived in the browser
          from GET /campaigns/:id/calls -- a route the Python API does not
          serve. The fetch failed, the catch logged to the console, and metrics
          stayed at their all-zero initial values, so this rendered "0 Total
          Calls" and a confident "0% Success Rate" for a campaign that had
          called two hundred people. The counts now come from
          /campaigns/:id/results, already classified, and a failed load says so
          instead of showing zeroes. */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>Results</Typography>
        {campaignId && <CampaignResults campaignId={campaignId} />}
      </Paper>


      {/* Contact List */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
          Contact List
        </Typography>
        {contactsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : contacts.length === 0 ? (
          <Alert severity="info">No contacts found for this campaign.</Alert>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Contact List</strong></TableCell>
                  <TableCell><strong>Phone Number</strong></TableCell>
                  <TableCell><strong>Name</strong></TableCell>
                  <TableCell><strong>Email</strong></TableCell>
                  <TableCell><strong>Custom Fields</strong></TableCell>
                  <TableCell><strong>Scheduled Time</strong></TableCell>
                  <TableCell><strong>Call Status</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {contacts.map((contact) => {
                  const customFieldsKeys = contact.custom_fields ? Object.keys(contact.custom_fields) : [];

                  return (
                    <TableRow key={contact.id}>
                      <TableCell>{contact.contact_list_name || '-'}</TableCell>
                      <TableCell>{contact.phone_number}</TableCell>
                      <TableCell>
                        {contact.first_name || contact.last_name
                          ? `${contact.first_name || ''} ${contact.last_name || ''}`.trim()
                          : '-'}
                      </TableCell>
                      <TableCell>{contact.email || '-'}</TableCell>
                      <TableCell>
                        {customFieldsKeys.length > 0 ? (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {customFieldsKeys.map((key) => (
                              <Chip
                                key={key}
                                label={`${key}: ${contact.custom_fields![key]}`}
                                size="small"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        {contact.call_status?.schedule_dt
                          ? format(new Date(contact.call_status.schedule_dt), 'MMM d, yyyy HH:mm')
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {contact.call_status ? (
                          <Box>
                            <Chip
                              label={contact.call_status.call_placed ? 'Placed' : contact.call_status.call_attempted ? 'Attempted' : 'Scheduled'}
                              size="small"
                              color={contact.call_status.call_placed ? 'success' : contact.call_status.call_attempted ? 'warning' : 'info'}
                            />
                          </Box>
                        ) : (
                          <Chip label="Not Scheduled" size="small" color="default" />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default CampaignDetails;
